import { UseQueryResult } from "@tanstack/react-query";

type ExtendedUseQueryResult<T> = UseQueryResult<T> & { isSuccess: boolean };

interface APIAction {
  name: string;
  uri: string;
  method: string;
}

interface APIObject {
  id: string;
  actions?: APIAction[];
  [key: string]: any; // placeholder for accepting custom columns as well
}

interface APILookupValue {
  id: number;
  name: string;
}

interface EntityData extends APIObject {
  [key: string]: any;
}

interface OptionalNameEntityData extends EntityData {
  name?: string;
}

interface EntitiesResponse<T extends EntityData> {
  [key: string]: T[] | undefined;
}

interface Aggregations {
  sum: number;
  min: number;
  max: number;
  avg: number;
  count: number;
}

interface AggregationField<T> {
  name: T;
  aggregations: Aggregations;
}

interface AggregationData<T> {
  fieldValue: string;
  /**
   * Rows in this group across the whole filtered set — a COUNT(*) the server
   * computes per group, unaffected by which pages have been fetched. Absent on
   * responses built client-side.
   */
  total_count?: number;
  fields: AggregationField<T>[];
}

interface GenericAggregations<T> {
  data: AggregationData<T>[];
  groupedBy: string | null;
}

interface Filters {
  [key: string]: any;
}
export const EmptyFilters: Filters = {};
export type FiltersSetter = (
  filtersOrUpdater: Filters | ((prev: Filters) => Filters)
) => void;
interface SearchQuery {
  term: string;
  fields?: string[];
}

interface SortQuery {
  field: string;
  ascending?: boolean;
}

interface QueryAggregations<T extends EntityData> {
  groupBy?: {
    field: keyof T;
    function?: string;
  };
}

interface QueryData<T extends EntityData, F extends Filters> {
  filters: F;
  aggregations?: QueryAggregations<T>;
  search?: SearchQuery | undefined;
  sort?: SortQuery | undefined;
}

interface EntityPage<T extends EntityData, A = any> {
  [key: string]: T[] | number | GenericAggregations<A> | undefined;
  total: number;
  page: number;
  size: number;
  pages: number;
  aggregations?: GenericAggregations<A>;
}

export enum ComputedFields {
  NONE = "none",
  MINIMAL = "minimal",
  ALL = "all"
}

export type {
  // API Usage Types
  ExtendedUseQueryResult,
  APIObject,
  APILookupValue,
  APIAction,
  EntityData,
  OptionalNameEntityData,
  EntitiesResponse,
  EntityPage,
  // Aggregation and Filter Types
  Filters,
  GenericAggregations,
  SearchQuery,
  SortQuery,
  QueryData,
  QueryAggregations,
  AggregationField,
  AggregationData
};
